/**
 * @author mrdoob / http://mrdoob.com/
 *
 * Modifications Copyright Higharc 2019
 *
 */
import { productLog } from "common/utils/logging";

export default class Stats {
  dom: HTMLElement;

  // This can be set to a positive number of seconds to log FPS measurements to the console.
  fpsMeasureInterval: number = -1;

  private mode: number;
  private frameBeginTime: number;
  private prevPanelUpdateTime: number;
  private prevFpsMeasureTime: number;

  private frames: number;
  private framesAtPanelUpdate: number;
  private framesAtMeasurement: number;

  private msPanel: StatsPanel;
  private fpsPanel: StatsPanel;
  private memPanel: StatsPanel | undefined;

  constructor() {
    this.mode = 0;

    const container = document.createElement("div");
    container.style.cssText = "position:fixed;top:0;left:0;cursor:pointer;opacity:0.9;z-index:10000";
    container.addEventListener(
      "click",
      (event) => {
        event.preventDefault();
        this.showPanel(++this.mode % container.children.length);
      },
      false,
    );
    this.dom = container;

    this.frameBeginTime = (performance || Date).now();
    this.prevPanelUpdateTime = this.frameBeginTime;
    this.prevFpsMeasureTime = this.frameBeginTime;
    this.frames = 0;
    this.framesAtPanelUpdate = 0;
    this.framesAtMeasurement = 0;

    this.fpsPanel = this.addPanel(new StatsPanel("FPS", "#0ff", "#002"));
    this.msPanel = this.addPanel(new StatsPanel("MS", "#0f0", "#020"));
    if (self.performance && (self.performance as any).memory) {
      this.memPanel = this.addPanel(new StatsPanel("MB", "#f08", "#201"));
    }

    this.showPanel(0);
  }

  private addPanel(panel) {
    this.dom.appendChild(panel.dom);
    return panel;
  }

  private showPanel(id) {
    for (let i = 0; i < this.dom.children.length; i++) {
      (this.dom.children[i] as HTMLElement).style.display = i === id ? "block" : "none";
    }

    this.mode = id;
  }

  begin() {
    this.frameBeginTime = (performance || Date).now();
  }

  end() {
    this.frames++;

    const time = (performance || Date).now();

    this.msPanel.update(time - this.frameBeginTime, 200);

    if (time >= this.prevPanelUpdateTime + 1000) {
      this.fpsPanel.update(((this.frames - this.framesAtPanelUpdate) * 1000) / (time - this.prevPanelUpdateTime), 100);

      this.prevPanelUpdateTime = time;
      this.framesAtPanelUpdate = this.frames;

      if (this.memPanel) {
        const memory = (performance as any).memory;
        this.memPanel.update(memory.usedJSHeapSize / 1048576, memory.jsHeapSizeLimit / 1048576);
      }
    }

    if (this.fpsMeasureInterval > 0 && time >= this.prevFpsMeasureTime + this.fpsMeasureInterval * 1000) {
      const frames = this.frames - this.framesAtMeasurement;
      const timeMs = time - this.prevFpsMeasureTime;
      productLog((frames * 1000) / timeMs + " FPS");

      this.prevFpsMeasureTime = time;
      this.framesAtMeasurement = this.frames;
    }

    return time;
  }
}

class StatsPanel {
  dom: HTMLElement;
  private canvas: HTMLCanvasElement;
  private context: CanvasRenderingContext2D | null;
  private round: (a: number) => number;
  private min: number;
  private max: number;

  private readonly measurements: any;

  constructor(
    private name: string,
    private fg: string,
    private bg: string,
  ) {
    (this.min = Infinity), (this.max = 0), (this.round = Math.round);
    const PR = Math.round(window.devicePixelRatio || 1);

    this.measurements = {
      WIDTH: 80 * PR,
      HEIGHT: 48 * PR,
      TEXT_X: 3 * PR,
      TEXT_Y: 2 * PR,
      GRAPH_X: 3 * PR,
      GRAPH_Y: 15 * PR,
      GRAPH_WIDTH: 74 * PR,
      GRAPH_HEIGHT: 30 * PR,
    };

    const canvas = document.createElement("canvas");
    canvas.width = this.measurements.WIDTH;
    canvas.height = this.measurements.HEIGHT;
    canvas.style.cssText = "width:80px;height:48px";

    this.canvas = canvas;
    this.dom = canvas;
    const context = canvas.getContext("2d");
    if (context === null) {
      // This is not really expected to happen, but in theory context initialization can fail.
      // We need to handle that to be compatible with strict null checks.
      // In the case of stats it's not a huge problem if initialization fails.
      console.warn("Initializing performance stats 2D context failed");
      this.context = null;
      return;
    }
    context.font = "bold " + 9 * PR + "px Helvetica,Arial,sans-serif";
    context.textBaseline = "top";

    context.fillStyle = this.bg;
    context.fillRect(0, 0, this.measurements.WIDTH, this.measurements.HEIGHT);

    context.fillStyle = this.fg;
    context.fillText(this.name, this.measurements.TEXT_X, this.measurements.TEXT_Y);
    context.fillRect(
      this.measurements.GRAPH_X,
      this.measurements.GRAPH_Y,
      this.measurements.GRAPH_WIDTH,
      this.measurements.GRAPH_HEIGHT,
    );

    context.fillStyle = this.bg;
    context.globalAlpha = 0.9;
    context.fillRect(
      this.measurements.GRAPH_X,
      this.measurements.GRAPH_Y,
      this.measurements.GRAPH_WIDTH,
      this.measurements.GRAPH_HEIGHT,
    );

    this.canvas = canvas;
    this.context = context;
    this.dom = canvas;
  }

  update(value, maxValue) {
    this.min = Math.min(this.min, value);
    this.max = Math.max(this.max, value);

    const context = this.context;
    if (context === null) {
      return;
    }
    context.fillStyle = this.bg;
    context.globalAlpha = 1;
    context.fillRect(0, 0, this.measurements.WIDTH, this.measurements.GRAPH_Y);
    context.fillStyle = this.fg;
    context.fillText(
      this.round(value) + " " + this.name + " (" + this.round(this.min) + "-" + this.round(this.max) + ")",
      this.measurements.TEXT_X,
      this.measurements.TEXT_Y,
    );

    context.drawImage(
      this.canvas,
      this.measurements.GRAPH_X + this.measurements.PR,
      this.measurements.GRAPH_Y,
      this.measurements.GRAPH_WIDTH - this.measurements.PR,
      this.measurements.GRAPH_HEIGHT,
      this.measurements.GRAPH_X,
      this.measurements.GRAPH_Y,
      this.measurements.GRAPH_WIDTH - this.measurements.PR,
      this.measurements.GRAPH_HEIGHT,
    );

    context.fillRect(
      this.measurements.GRAPH_X + this.measurements.GRAPH_WIDTH - this.measurements.PR,
      this.measurements.GRAPH_Y,
      this.measurements.PR,
      this.measurements.GRAPH_HEIGHT,
    );

    context.fillStyle = this.bg;
    context.globalAlpha = 0.9;
    context.fillRect(
      this.measurements.GRAPH_X + this.measurements.GRAPH_WIDTH - this.measurements.PR,
      this.measurements.GRAPH_Y,
      this.measurements.PR,
      this.round((1 - value / maxValue) * this.measurements.GRAPH_HEIGHT),
    );
  }
}
